import z from "zod";
import { Money } from "../../../services/models/cart.models";
import { GoogleAnalytics4EventModelSchema } from "../GoogleAnalytics/GoogleAnalyticsSchema";
import {
    FEEDBACK_TEXT,
    MAX_FULLNAME_LENGTH,
    validationHelper,
} from "../../validationHelpers";

export const MoneySchema = z.object({
    amount: z.number(),
    currency: z.string(),
});

export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1, { message: FEEDBACK_TEXT().REQUIRED })
    .email({ message: FEEDBACK_TEXT().INVALID });

export const firstNameSchema = z
    .string()
    .trim()
    .min(1, { message: FEEDBACK_TEXT().REQUIRED })
    .superRefine((value, context) => {
        if (validationHelper.containsNumbers(value)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().CANNOT_CONTAIN_NUMBERS,
            });
        }
        if (
            validationHelper.containsDisallowedSpecialCharacters(
                "firstName",
                value,
            )
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().CANNOT_CONTAIN_SPECIAL_CHARACTERS,
            });
        }
    });

export const lastNameSchema = z
    .string()
    .trim()
    .min(1, { message: FEEDBACK_TEXT().REQUIRED })
    .superRefine((value, context) => {
        if (validationHelper.containsNumbers(value)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().CANNOT_CONTAIN_NUMBERS,
            });
        }
        if (
            validationHelper.containsDisallowedSpecialCharacters(
                "lastName",
                value,
            )
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().CANNOT_CONTAIN_SPECIAL_CHARACTERS,
            });
        }
    });

export const phoneNumberSchema = z
    .string()
    .trim()
    .superRefine((value, context) => {
        if (value.length < 1) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().REQUIRED,
            });
        }
        if (value.length != 8) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().PHONE_NUMBER_REQUIRED_LENGTH,
            });
        }
        if (validationHelper.containsSpecialCharacters(value)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().CANNOT_CONTAIN_SPECIAL_CHARACTERS,
            });
        }
        if (!validationHelper.isPhoneNumber(value)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().INVALID,
            });
        }
    });

export const addressSchema = z
    .string()
    .trim()
    .min(1, { message: FEEDBACK_TEXT().REQUIRED })
    .superRefine((value, context) => {
        if (
            validationHelper.containsDisallowedSpecialCharacters(
                "address",
                value,
            )
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().CANNOT_CONTAIN_SPECIAL_CHARACTERS,
            });
        }
    });

export const postalCodeSchema = z
    .string()
    .trim()
    .min(1, { message: FEEDBACK_TEXT().REQUIRED })
    .superRefine((value, context) => {
        if (value.length !== 4) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().POSTAL_CODE_REQUIRED_LENGTH,
            });
        }
        if (!validationHelper.isPostalCode(value)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().POSTAL_CODE_REQUIRED_LENGTH,
            });
        }
    });

export const citySchema = z
    .string()
    .trim()
    .min(1, { message: FEEDBACK_TEXT().REQUIRED })
    .superRefine((value, context) => {
        if (validationHelper.containsNumbers(value)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().CANNOT_CONTAIN_NUMBERS,
            });
        }
        if (
            validationHelper.containsDisallowedSpecialCharacters("city", value)
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: FEEDBACK_TEXT().CANNOT_CONTAIN_SPECIAL_CHARACTERS,
            });
        }
    });

// Click and Collect form schema
export type ClickAndCollectForm = z.infer<typeof ClickAndCollectFormSchema>;
export const ClickAndCollectFormSchema = z.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    email: emailSchema,
    phoneNumber: phoneNumberSchema,
});

// Full submit-time validation, including the combined full name length check.
export const ClickAndCollectFormWithFullNameCheckSchema = ClickAndCollectFormSchema.superRefine((data, ctx) => {
    if (
        (data.firstName?.length || 0) + (data.lastName?.length || 0) >=
        MAX_FULLNAME_LENGTH
    ) {
        ctx.addIssue({
            path: ["firstName"],
            code: z.ZodIssueCode.custom,
            message: FEEDBACK_TEXT().NAME_MAXLENGTH_EXCEEDED,
        });
        ctx.addIssue({
            path: ["lastName"],
            code: z.ZodIssueCode.custom,
            message: FEEDBACK_TEXT().NAME_MAXLENGTH_EXCEEDED,
        });
    }
});

// Click and Collect reservation request/response (POST /api/cart/reserve)
export type ReserveCartRequest = z.infer<typeof ReserveCartRequestSchema>;
export const ReserveCartRequestSchema = z.object({
    warehouseCode: z.string(),
    customerName: z.string(),
    customerEmail: z.string(),
    customerPhone: z.string(),
});

export type ClickCollectOrder = z.infer<typeof ClickCollectOrderSchema>;
export const ClickCollectOrderSchema = z.object({
    orderNumber: z.string().nullable().optional(),
});

export type ReserveCartResponse = z.infer<typeof ReserveCartResponseSchema>;
export const ReserveCartResponseSchema = z.object({
    createdOk: z.boolean(),
    errorMessage: z.string().nullable().optional(),
    orderNumber: z.string().nullable().optional(),
});

export type KeySellingPoint = z.infer<typeof KeySellingPointSchema>;
export const KeySellingPointSchema = z.object({
    img: z.string(),
    heading: z.string(),
    description: z.string(),
});

export type CheckoutLoginCta = z.infer<typeof CheckoutLoginCtaSchema>;
export const CheckoutLoginCtaSchema = z.object({
    enabled: z.boolean(),
    heading: z.string(),
    content: z.string(),
});

export type PaymentMethodPreview = z.infer<typeof PaymentMethodPreviewSchema>;
export const PaymentMethodPreviewSchema = z.object({
    paymentMethodId: z.string(),
    systemKeyword: z.string(),
    imageId: z.string(),
});

export type LineItemModel = z.infer<typeof LineItemSchema>;
export const LineItemSchema = z.object({
    name: z.string().toLowerCase(),
    brand: z.string(),
    quantity: z.number(),
    code: z.string(),
    color: z.string(),
    size: z.string(),
    listPrice: MoneySchema,
    discountedPrice: MoneySchema,
    imageUrl: z.string().optional(),
    altText: z.string().optional(),
    productUrl: z.string().optional(),
    ean: z.string(),
    currentInventory: z.number(),
    promotionsText: z.string().optional().nullable(),    
    memberPromotionsText: z.string().optional().nullable(),    
    memberPromotionsLink: z.string().optional().nullable(),
    esalesKey: z.string(),
});

export type Product = z.infer<typeof ProductSchema>;
export const ProductSchema = z.object({
    header: z.string(),
    name: z.string(),
    count: z.number(),
    size: z.string(),
    color: z.string(),
    shippingText: z.string(),
});

export type Cart = z.infer<typeof CartSchema>;
export const CartSchema = z.object({
    id: z.string(),
    itemCount: z.number(),
    total: MoneySchema,
    subTotal: MoneySchema,
    subTotalWithoutDiscounts: MoneySchema,
    discountTotal: MoneySchema,
    selectedShippingMethodPrice: MoneySchema,
    lineItems: z.array(LineItemSchema),
    appliedCoupons: z.record(z.string(), z.string()).optional(),
    paymentMethods: z.array(PaymentMethodPreviewSchema),
    freeShippingActive: z.boolean(),
    freeShippingLimit: MoneySchema,
    isAuthenticated: z.boolean(),
    url: z.string(),
});

export type AddCouponResponseModel = z.infer<typeof AddCouponResponseModelSchema>;
export const AddCouponResponseModelSchema = z.discriminatedUnion("couponAdded", [
    z.object({
        couponAdded: z.literal(true),
        couponNotAddedInfo: z.string().optional().nullable(),
        cart: CartSchema,
    }),
    z.object({
        couponAdded: z.literal(false),
        couponNotAddedInfo: z.string(),
        cart: CartSchema.optional().nullable(),
    }),
]);

export type AddressModel = z.infer<typeof AddressModelSchema>;
export const AddressModelSchema = z.object({
    email: emailSchema,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    phoneNumber: phoneNumberSchema,
    address: addressSchema,
    postalCode: postalCodeSchema,
    city: citySchema,
    bringAddressId: z.string().optional()
});

export type CheckoutInputModel = z.infer<typeof CheckoutInputModelSchema>;
export const CheckoutInputModelSchema = z.object({
    billingAddress: AddressModelSchema.partial(),
    shippingAddress: AddressModelSchema.partial(),
});

export type AddressModelWithoutAnalytics = z.
infer<typeof AddressModelWithoutAnalyticsSchema>;
export const AddressModelWithoutAnalyticsSchema = AddressModelSchema.superRefine((data, ctx) => {
    if (
        (data.firstName?.length || 0) + (data.lastName?.length || 0) >=
        MAX_FULLNAME_LENGTH
    ) {
        ctx.addIssue({
            path: ["firstName"],
            code: z.ZodIssueCode.custom,
            message: FEEDBACK_TEXT().NAME_MAXLENGTH_EXCEEDED,
        });
        ctx.addIssue({
            path: ["lastName"],
            code: z.ZodIssueCode.custom,
            message: FEEDBACK_TEXT().NAME_MAXLENGTH_EXCEEDED,
        });
    }
});

export type PlaceholderAddressModel = z.infer<
    typeof PlaceholderAddressModelSchema
>;
export const PlaceholderAddressModelSchema = z.object({
    ...AddressModelSchema.shape,
    email: z.string().min(0).optional(),
    firstName: z.string().min(0).optional(),
    lastName: z.string().min(0).optional(),
    phoneNumber: z.string().min(0).optional(),
    address: z.string().min(0).optional(),
    postalCode: z.string().min(0).optional(),
    city: z.string().min(0).optional(),
    loyaltyClubId: z.string().min(0).optional(),
    bringAddressId: z.string().min(0).optional(),
});

export type LoginModel = z.infer<typeof LoginModelSchema>;
export const LoginModelSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, { message: FEEDBACK_TEXT().REQUIRED }),
});

export type OrderConfirmation = z.infer<typeof OrderConfirmationSchema>;
export const OrderConfirmationSchema = z.object({
    addressModel: AddressModelSchema,
    estimatedDeliveryTime: z.string(),
    paymentOption: z.string(),
    orderConfirmationText: z.string(),
    customerServiceText: z.string(),
    errorState: z.string().optional(),
    itemCount: z.string(),
    total: z.custom<Money>(),
    shippingCost: z.custom<Money>(),
    cartItems: z.array(LineItemSchema),
});

export type GetCartResponseModel = z.infer<typeof GetCartResponseModelSchema>;
export const GetCartResponseModelSchema = z.object({
    cartDto: CartSchema,
});

export type UpdateProductQuantityResponseModel = z.infer<typeof UpdateProductQuantityResponseModelSchema>;
export const UpdateProductQuantityResponseModelSchema = z.object({
    cartDto: CartSchema.nullable(),
    dataLayerObject: GoogleAnalytics4EventModelSchema.nullable().optional(),
    updateCartError: z.boolean().default(false),
    errorMessage: z.string().nullable().optional(),
    isStockError: z.boolean().optional(),
});